import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronRight } from "lucide-react";
import { buildPersonaAppLanding } from "../lib/personas";
import { dependencySystemNodes, linkedApplicationRows } from "../lib/dependencyApps";
import { withApp } from "../lib/story";

/** Persona-specific application analytics (post-G1 or specialist roles). */
export default function PersonaAppPanel({ app, guide, portfolio }) {
  const landing = buildPersonaAppLanding(app, guide);
  const [depsOpen, setDepsOpen] = useState(false);
  if (!landing?.tiles?.length) return null;

  const dependentApps = linkedApplicationRows(app, portfolio);
  const systems = dependencySystemNodes(app);

  return (
    <section className="feature-panel analytics-panel persona-app-panel" data-testid="persona-app-panel" data-persona={landing.persona.id}>
      <div className="panel-header compact">
        <div>
          <p className="kicker">{landing.persona.kicker} · APPLICATION</p>
          <h2>{landing.title}</h2>
          <p className="muted small-note">{landing.persona.mainQuestion}</p>
        </div>
      </div>

      <div className="persona-kpi-grid metric-grid compact">
        {landing.tiles.map((tile) => {
          const isDeps = tile.drill === "dependencies" && Number(tile.value) > 0;
          const inner = (
            <div>
              <small>{tile.label}</small>
              <strong>{tile.value}</strong>
              <span>{tile.detail}</span>
            </div>
          );
          if (isDeps) {
            return (
              <button
                key={tile.label}
                type="button"
                className={`metric metric-drill tone-${tile.tone}${depsOpen ? " metric-active" : ""}`}
                data-testid="persona-app-kpi-dependencies"
                aria-expanded={depsOpen}
                onClick={() => setDepsOpen((open) => !open)}
              >
                {inner}
                <ChevronRight size={16} className={`metric-chevron${depsOpen ? " metric-chevron-open" : ""}`} />
              </button>
            );
          }
          return (
            <div key={tile.label} className={`metric tone-${tile.tone}`} data-testid={`persona-app-kpi-${tile.label.toLowerCase().replace(/\s+/g, "-")}`}>
              {inner}
            </div>
          );
        })}
      </div>

      {depsOpen && (
        <section className="persona-deps-panel" data-testid="persona-dependent-apps">
          <div className="panel-header compact">
            <div>
              <p className="kicker">DEPENDENCY BLAST RADIUS · {app.id}</p>
              <h3>{systems.length} mapped systems · {dependentApps.length} coupled applications</h3>
            </div>
            <Link className="secondary-button small-btn" to={withApp("/decision?focus=dependencies", app.id)} data-testid="persona-deps-full-drill">
              Full dependency map <ChevronRight size={14} />
            </Link>
          </div>
          {dependentApps.length > 0 ? (
            <ul className="persona-dep-app-list" data-testid="persona-dependent-app-list">
              {dependentApps.map((row) => (
                <li key={row.id} data-testid={`persona-dependent-app-${row.id.toLowerCase()}`}>
                  <div>
                    <strong>{row.id}</strong>
                    <span>{row.name}</span>
                    {row.recommendation && <small className="muted">{row.recommendation}</small>}
                  </div>
                  <Link className="text-button" to={withApp("/overview", row.id)} data-testid={`persona-open-dependent-${row.id.toLowerCase()}`}>
                    Open <ArrowUpRight size={13} />
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted small-note">No other applications in the blast radius — only local systems are mapped.</p>
          )}
          {systems.length > 0 && (
            <div className="persona-dep-systems" data-testid="persona-dependent-systems">
              <p className="kicker">MAPPED SYSTEMS</p>
              <ul className="admin-list inline-tags">
                {systems.map((node) => (
                  <li key={node.id}>{node.label || node.id}</li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      {landing.applicableStandards.length > 0 && (
        <div className="persona-standards" data-testid="persona-applicable-standards">
          <p className="kicker">APPLICABLE</p>
          <ul className="admin-list inline-tags">
            {landing.applicableStandards.map((s) => <li key={s}>{s}</li>)}
          </ul>
        </div>
      )}

      {landing.nextAction && (
        <div className="persona-next-action inline" data-testid="persona-app-next-action">
          <p className="kicker">NEXT ACTION</p>
          <strong>{landing.nextAction.label}</strong>
          <p className="muted small-note">{landing.nextAction.detail}</p>
          <Link className="primary-button" to={landing.route(landing.nextAction.route)} data-testid="persona-app-next-link">
            {landing.nextAction.label} <ArrowUpRight size={15} />
          </Link>
        </div>
      )}
    </section>
  );
}
